/**
 * Delt innloggingskontrakt (#139).
 *
 * Modellen: brukernavn + passord er innloggingen overalt. PIN er bare
 * hurtigbytte på en innrullert kjøkkenskjerm (`stell_device`-cookie, #142).
 * Adminopplåsing ber om adminens eget passord (#143).
 *
 * Brukernavn er unike per familie (FF-09, #177, epic #167 D4) og lagres
 * normalisert (`normalizeUsername`), så «Mamma» og «mamma» er samme konto.
 * Bare ASCII — det holder unna navn som ser like ut men ikke er det;
 * visningsnavn beholder æøå. Familien velges med familiekoden.
 */
import { z } from 'zod';

// ─── Brukernavn ─────────────────────────────────────────────────────────────
export const USERNAME_MIN = 3;
export const USERNAME_MAX = 32;
/** Starter med bokstav; deretter a–z, 0–9, punktum, bindestrek, understrek. */
export const USERNAME_PATTERN = /^[a-z][a-z0-9._-]{2,31}$/;

export function normalizeUsername(raw: string): string {
  return raw.trim().toLowerCase();
}

export const Username = z
  .string()
  .transform(normalizeUsername)
  .pipe(z.string().regex(USERNAME_PATTERN, 'invalid_username'));

// ─── Familiekode (FF-09, #177, D5) ──────────────────────────────────────────
// Familiekoden er `families.slug`, valgt av systemadmin: a–z, 0–9 og
// bindestrek, 3–32 tegn, starter med bokstav. Aldri visningsnavnet.
export const FAMILY_CODE_MIN = 3;
export const FAMILY_CODE_MAX = 32;
export const FAMILY_CODE_PATTERN = /^[a-z][a-z0-9-]{2,31}$/;

export function normalizeFamilyCode(raw: string): string {
  return raw.trim().toLowerCase();
}

export function isValidFamilyCode(code: string): boolean {
  return FAMILY_CODE_PATTERN.test(code);
}

export const FamilyCode = z
  .string()
  .transform(normalizeFamilyCode)
  .pipe(z.string().regex(FAMILY_CODE_PATTERN, 'invalid_family_code'));

// ─── Passord ────────────────────────────────────────────────────────────────
// NIST 800-63B: lengde teller, sammensetningsregler gjør ikke. Sjekken mot
// brukernavnet og lista over vanlige passord ligger på serveren
// (`apps/api/src/util/password-policy.ts`).
export const PASSWORD_MIN = 8;
export const PASSWORD_MAX = 128;
export const Password = z.string().min(PASSWORD_MIN, 'too_short').max(PASSWORD_MAX, 'too_long');

/**
 * Hvorfor et passord ble avvist (#140). Alle endepunkt som setter et passord
 * svarer 400 `{ error: 'weak_password', reason }` — også når det er
 * Zod-lengdesjekken over som slår ut.
 */
/** `same_as_current` (#143): et passordbytte må gi et annet passord enn dagens. */
export type PasswordPolicyReason =
  | 'too_short'
  | 'too_long'
  | 'same_as_username'
  | 'common'
  | 'same_as_current';

export interface WeakPasswordError {
  error: 'weak_password';
  reason: PasswordPolicyReason;
}

// ─── POST /api/auth/login ──────────────────────────────────────────────────
// Brukernavnet og familiekoden valideres bevisst ikke mot mønstrene her: et
// ugyldig navn skal gi samme `invalid_credentials` som et ukjent, ikke en 400
// som avslører hvilke former som finnes.
//
// `family` er valgfri (FF-09): uten den gjelder familien til den innrullerte
// kjøkkenskjermen, ellers den eneste familien som har medlemmer. Slik virker
// innloggingen på en instans med én familie før noen har valgt en kode, og
// eldre klienter som ikke sender feltet, virker som før.
export const LoginBody = z.object({
  family: z.string().max(200).optional(),
  username: z.string().min(1).max(200),
  password: z.string().min(1).max(PASSWORD_MAX),
  deviceLabel: z.string().max(120).optional(),
});
export type LoginBodyInput = z.infer<typeof LoginBody>;

/**
 * Feilkoder fra /auth/login og /auth/pin. 401, 429 ved IP-grense, og 403
 * `family_suspended` når passordet er riktig men familien er suspendert
 * (FF-09) — det avslører bare noe til den som har riktig passord.
 */
export type LoginError = 'invalid_credentials' | 'locked' | 'rate_limited' | 'family_suspended';

/** Trygge felt om innlogget medlem — aldri hash, allergier eller kosthold. */
export interface AuthMember {
  id: string;
  username: string | null;
  name: string;
  short_name: string;
  role: 'admin' | 'teen' | 'child';
  color_token: string;
  /**
   * #143: medlemmet har fortsatt det arvede husstandspassordet (satt før
   * #140, aldri byttet) og må velge sitt eget før appen kan brukes. Til da
   * avviser adminopplåsingen med 403 `password_change_required`.
   */
  must_change_password: boolean;
  /**
   * FF-11 (#179): familien er opprettet av systemadminen, og denne adminen har
   * ikke fullført oppsettet. Appen sender hen til veiviseren til det er gjort.
   * Alltid false for andre enn admin.
   */
  family_setup_pending: boolean;
}

export interface LoginResponse {
  ok: true;
  member: AuthMember;
}

// ─── POST /api/auth/password — bytt eget passord ───────────────────────────
// Avslutter alle andre økter for medlemmet; økten som byttet beholdes.
export const PasswordChangeBody = z
  .object({
    current: z.string().min(1).max(PASSWORD_MAX),
    next: Password,
    nextConfirm: z.string(),
  })
  .refine((v) => v.next === v.nextConfirm, {
    message: 'password_mismatch',
    path: ['nextConfirm'],
  });
export type PasswordChangeInput = z.infer<typeof PasswordChangeBody>;

// ─── PUT /api/auth/pin-code — sett/bytt egen PIN (krever passord) ──────────
export const PinSetBody = z
  .object({
    password: z.string().min(1).max(PASSWORD_MAX),
    pin: z.string().regex(/^\d{4,8}$/, 'PIN must be 4-8 digits'),
    pinConfirm: z.string(),
  })
  .refine((v) => v.pin === v.pinConfirm, {
    message: 'PIN confirmation must match',
    path: ['pinConfirm'],
  });
export type PinSetInput = z.infer<typeof PinSetBody>;

// ─── Tilbakestilling av passord (admin-utstedt lenke, #140) ────────────────
// POST /api/members/:id/password-reset          → PasswordResetCreateResult
// GET  /api/auth/password-reset/:token          → PasswordResetPreview
// POST /api/auth/password-reset/:token/accept   → { ok: true } (logger IKKE inn)
export interface PasswordResetCreateResult {
  memberId: string;
  memberName: string;
  resetUrl: string; // `/nytt-passord/:token`
  expiresAt: string;
}

export interface PasswordResetPreview {
  memberName: string;
  username: string | null;
  expiresAt: string;
}

export const PasswordResetAccept = z
  .object({
    password: Password,
    passwordConfirm: z.string(),
  })
  .refine((v) => v.password === v.passwordConfirm, {
    message: 'password_mismatch',
    path: ['passwordConfirm'],
  });
export type PasswordResetAcceptInput = z.infer<typeof PasswordResetAccept>;

// ─── Innrullerte enheter (#142) ─────────────────────────────────────────────
// POST /api/devices/enroll   (requireAdminScope) → setter `stell_device`, DeviceRow
// GET  /api/devices          (requireAdminScope) → { devices: DeviceRow[] }
// POST /api/devices/:id/revoke (requireAdminScope)
// GET  /api/auth/device      (authless)          → DeviceStatus
export const DEVICE_COOKIE_NAME = 'stell_device' as const;

export const DeviceEnrollBody = z.object({
  label: z.string().trim().min(1).max(60),
});
export type DeviceEnrollInput = z.infer<typeof DeviceEnrollBody>;

export interface DeviceRow {
  id: string;
  label: string;
  enrolled_by_name: string | null;
  created_at: string;
  last_seen_at: string;
  revoked_at: string | null;
  /** Sant for enheten som gjør forespørselen. */
  current: boolean;
}

export type DeviceStatus = { enrolled: false } | { enrolled: true; label: string };

// ─── GET /api/auth/roster — krever innrullert enhet (#142) ──────────────────
// Erstatter /api/members/basic. Uten gyldig `stell_device`: 404.
export interface RosterMember {
  id: string;
  name: string;
  short_name: string;
  role: 'admin' | 'teen' | 'child';
  color_token: string;
  /** Usant → klienten sender til passordskjemaet med brukernavnet utfylt. */
  has_pin: boolean;
  username: string | null;
}
